/**
 * @fileoverview Istatistik Hesaplama Fonksiyonlari
 *
 * Bu modul ilan listelerinden fiyat istatistikleri hesaplamak icin
 * yardimci fonksiyonlar saglar.
 *
 * Fonksiyonlar gecersiz/eksik veri durumlarini guvenli sekilde
 * ele alir ve sadece gecerli fiyat degerleri uzerinden hesaplama yapar.
 *
 * @module utils/stats
 *
 * @example
 * import { calculatePriceStats } from './stats';
 *
 * const stats = calculatePriceStats(listings);
 * if (stats) {
 *   console.log(`Ortalama fiyat: ${stats.ortalama} TL`);
 *   console.log(`Fiyat araligi: ${stats.min} - ${stats.max} TL`);
 * }
 */

/**
 * Ilan listesinden fiyat istatistiklerini hesaplar.
 *
 * Bu fonksiyon:
 * - Null/undefined listeyi guvenli sekilde ele alir
 * - Sadece gecerli fiyat degerlerini (number, finite, pozitif) kullanir
 * - Gecerli fiyat yoksa null dondurur
 * - Ortalama degerini yuvarlar (tam sayi)
 *
 * Gecerli fiyat kriterleri:
 * - typeof === 'number'
 * - Number.isFinite() === true
 * - Deger > 0 (pozitif)
 *
 * @param {Array<Object>} listings - Ilan listesi
 * @param {number} [listings[].price] - Ilan fiyati (TRY)
 * @returns {{ min: number, max: number, ortalama: number, count: number }|null}
 *   Istatistikler veya null (gecerli fiyat yoksa)
 *
 * @property {number} min - En dusuk fiyat (TRY)
 * @property {number} max - En yuksek fiyat (TRY)
 * @property {number} ortalama - Ortalama fiyat (TRY, yuvarlanmis)
 * @property {number} count - Gecerli fiyati olan ilan sayisi
 *
 * @example
 * // Gecerli liste:
 * const stats = calculatePriceStats([
 *   { id: 1, price: 1000000 },
 *   { id: 2, price: 2000000 },
 *   { id: 3, price: 1500000 }
 * ]);
 * // Sonuc: { min: 1000000, max: 2000000, ortalama: 1500000, count: 3 }
 *
 * @example
 * // Bos liste:
 * calculatePriceStats([])
 * // Sonuc: null
 *
 * @example
 * // Karisik gecerlilik:
 * const stats = calculatePriceStats([
 *   { id: 1, price: 1000000 },      // gecerli
 *   { id: 2, price: null },          // gecersiz - null
 *   { id: 3, price: -500 },          // gecersiz - negatif
 *   { id: 4, price: "2000000" },     // gecersiz - string
 *   { id: 5, price: 3000000 }        // gecerli
 * ]);
 * // Sonuc: { min: 1000000, max: 3000000, ortalama: 2000000, count: 2 }
 *
 * @example
 * // Null/undefined input:
 * calculatePriceStats(null)
 * // Sonuc: null
 *
 * calculatePriceStats(undefined)
 * // Sonuc: null
 */
export function calculatePriceStats(listings) {
  // Fiyatlari cikar ve filtrele
  // Sadece gecerli fiyatlari (number, finite, pozitif) al
  const prices = (listings || [])
    .map((l) => l?.price)
    .filter((p) => typeof p === "number" && Number.isFinite(p) && p > 0);

  // Gecerli fiyat yoksa null dondur
  if (prices.length === 0) return null;

  // Istatistikleri hesapla
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
    ortalama: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
    count: prices.length,
  };
}
